import React, { useState } from "react";
import { FormattedMessage as T } from "react-intl";
import { useQuery } from "react-query";

import {
  Box,
  Button,
  CircularProgress,
  Divider,
  IconButton,
  InputAdornment,
  TextField,
  Typography,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import ClearIcon from "@mui/icons-material/Clear";
import SearchIcon from "@mui/icons-material/Search";

import useGlobalContext from "hooks/globalData";
import useDebounce from "hooks/useDebounce";
import {
  ListRequestOfBaseFilter,
  ListResponseOfWidget,
  Widget,
} from "services/KnowfullyAdminClient";

type WidgetSearchProps = {
  selectedWidgetIds: string[];
  onAddWidget: (widget: Widget) => void;
};

const WidgetSearch = ({
  onAddWidget,
  selectedWidgetIds,
}: WidgetSearchProps) => {
  const { widgetService } = useGlobalContext();

  const [searchTerm, setSearchTerm] = useState<string>("");
  const trimmedSearchTerm = searchTerm.trim();
  const debouncedSearchTerm = useDebounce(trimmedSearchTerm, 500);

  const { isError, data, error, isFetching } = useQuery<ListResponseOfWidget>(
    ["widgets", debouncedSearchTerm],
    () =>
      widgetService.getWidgets(
        ListRequestOfBaseFilter.fromJS({
          parameters: {
            searchText: debouncedSearchTerm,
            excludedIds: selectedWidgetIds,
          },
        })
      ),
    {
      enabled: debouncedSearchTerm !== "",
    }
  );

  if (isError) {
    console.log(error);
  }

  const handleAddWidget = (widget: Widget) => () => {
    setSearchTerm("");
    onAddWidget(widget);
  };

  const noResults =
    debouncedSearchTerm !== "" &&
    !isFetching &&
    (data?.totalRecords ?? 0) === 0;

  const hasResults =
    debouncedSearchTerm !== "" && (data?.totalRecords ?? 0) > 0;

  return (
    <>
      <Box mb={3}>
        <TextField
          fullWidth
          variant="outlined"
          value={searchTerm ?? ""}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon />
              </InputAdornment>
            ),
            endAdornment: (
              <InputAdornment position="end">
                <IconButton onClick={() => setSearchTerm("")}>
                  <ClearIcon />
                </IconButton>
              </InputAdornment>
            ),
          }}
          onChange={(e) => setSearchTerm(e.target.value)}
        />
      </Box>

      {isFetching && (
        <Box textAlign="center" my={2}>
          <CircularProgress size={30} />
        </Box>
      )}

      {noResults && (
        <Box textAlign="center" my={2}>
          <Typography>
            <T id="noWidgetsFound" />
          </Typography>
        </Box>
      )}

      {hasResults &&
        data!.data!.map((widget) => (
          <React.Fragment key={widget.id}>
            <Box py={2} display="flex" alignItems="center">
              <Box mr={2} flexGrow={1}>
                <div>{widget.name}</div>
              </Box>
              <Box mr={2} flexGrow={1}>
                <div>{widget.codeConstant}</div>
              </Box>
              <Box flexShrink={1}>
                <Button
                  disabled={isFetching}
                  variant="outlined"
                  startIcon={<AddIcon />}
                  size="small"
                  onClick={handleAddWidget(widget)}
                >
                  <T id="addWidgetButton" />
                </Button>
              </Box>
            </Box>
            <Divider />
          </React.Fragment>
        ))}
    </>
  );
};

export default WidgetSearch;
